/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useEffect, useMemo } from 'react'

import { EuiFormRow, EuiSelect } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useGetSaasProvidersQuery } from '@modules/cloud-lib/providers/hooks'
import type { CSP } from '@modules/ui-types/providers'

type Props = {
  value: string | undefined
  onChange: undefined | ((value: string) => void)
  csp: CSP | ''
  isDisabled: boolean
}

const HostedRegionField: React.FunctionComponent<Props> = ({
  value,
  onChange,
  csp,
  isDisabled,
}) => {
  const { data, isLoading } = useGetSaasProvidersQuery()

  const options = useMemo(
    () =>
      (
        data?.filter((provider) => provider.name === csp).flatMap((provider) => provider.regions) ||
        []
      ).map((region) => ({
        value: region.identifier,
        text: region.name,
      })),
    [data, csp],
  )

  // Set the region if we don't have one yet, and *reset* the region if what we do have is no longer valid (e.g. CSP changed)
  useEffect(() => {
    if (value && options.some((option) => option.value === value)) {
      return
    }

    const [firstOption] = options

    if (firstOption) {
      onChange?.(firstOption.value)
    }
  }, [options, value, onChange])

  return (
    <EuiFormRow
      label={<FormattedMessage id='trafficFilterList.hostedRegion.label' defaultMessage='Region' />}
      helpText={
        <FormattedMessage
          id='trafficFilterList.hostedRegion.help'
          defaultMessage='This policy can be applied only to deployments in the selected region.'
        />
      }
    >
      <EuiSelect
        options={options}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        isLoading={isLoading}
        disabled={isDisabled}
      />
    </EuiFormRow>
  )
}

export default HostedRegionField
